import { BaseService } from "./base.service";
import {
  PayrollFormValues,
  PayrollRequest,
  PayrollResponse,
  PayrollV2,
  AxiosParams,
  ApiResponseOne,
  ApiResponseList,
} from "../shared/types";
import hvpAuthenticatedApi from "../api/hvpAuthenticatedApi";
import { PayrollConcept } from "../shared/value-objects/hr.vo";

/**
 * The subset of payroll fields that users can edit directly.
 * Everything else is calculated by the V2 engine.
 */
export interface PayrollEditableOverrides {
  specialBonuses?: PayrollConcept[];
  endYearBonus?: number;
  extraVariableCompensations?: PayrollConcept[];
  profitSharing?: number;
  otherFixedDeductions?: PayrollConcept[];
  otherVariableDeductions?: PayrollConcept[];
}

const API_URL = "/payrolls";

export interface V2ComparisonDetail {
  payrollId: string;
  employee: string;
  code: string;
  periodStart: string;
  periodEnd: string;
  v1NetPay: number;
  v2NetPay: number;
  delta: number;
  v1Punctuality: number;
  v2Punctuality: number;
  v1VacComp: number;
  v2VacComp: number;
  v1Aguinaldo: number;
  v2Aguinaldo: number;
  v1GuaranteedIncome: number;
  v2GuaranteedIncome: number;
  v1ISR: number;
  v2ISR: number;
  deltaISR: number;
  v1IMSS: number;
  v2IMSS: number;
}

export interface V2ComparisonSummary {
  year: number;
  totalPayrolls: number;
  v1TotalNetPay: number;
  v2TotalNetPay: number;
  totalDelta: number;
}

export interface V2ComparisonResponse {
  ok: boolean;
  data: {
    summary: V2ComparisonSummary;
    details: V2ComparisonDetail[];
  };
}


/** Collaborators whose payroll the bulk creation could not create, with the reason (GH117). */
export interface BulkCreateV2Failure {
  collaboratorId: string;
  error: string;
}

/** Created, but something to check before paying, e.g. an aguinaldo of $0 (GH117). */
export interface BulkCreateV2Warning {
  collaboratorId: string;
  message: string;
}

export interface BulkCreateV2Response extends ApiResponseList<PayrollResponse> {
  /** Absent from backends older than GH117. */
  failures?: BulkCreateV2Failure[];
  warnings?: BulkCreateV2Warning[];
}

export class PayrollService extends BaseService<
  PayrollRequest,
  PayrollResponse,
  PayrollFormValues
> {
  constructor() {
    super(API_URL);
  }

  public async previewPayroll(
    collaboratorId: string,
    periodStartDate: string,
    periodEndDate: string,
    options?: { calculateYearEndBonus?: boolean }
  ): Promise<ApiResponseList<PayrollResponse>> {
    const response = await hvpAuthenticatedApi.post<
      ApiResponseList<PayrollResponse>
    >(`${API_URL}/preview`, {
      collaboratorId,
      periodStartDate,
      periodEndDate,
      ...options,
    });
    return response.data;
  }

  public async bulkSave(
    payrolls: PayrollRequest[]
  ): Promise<ApiResponseList<PayrollResponse>> {
    const response = await hvpAuthenticatedApi.post<
      ApiResponseList<PayrollResponse>
    >(`${API_URL}/bulk`, payrolls);
    return response.data;
  }

  public async getPayrollsByPeriod(
    startDate: string,
    endDate: string
  ): Promise<ApiResponseList<PayrollResponse>> {
    const params = {
      $periodStartDate: `$gte:${startDate}`,
      $periodEndDate: `$lte:${endDate}`,
    };
    const response = await hvpAuthenticatedApi.get<
      ApiResponseList<PayrollResponse>
    >(`${API_URL}`, { params });
    return response.data;
  }

  public async getPayrollsByCollaborator(
    collaboratorId: string,
    params?: AxiosParams
  ): Promise<ApiResponseList<PayrollResponse>> {
    const response = await hvpAuthenticatedApi.get<
      ApiResponseList<PayrollResponse>
    >(`${API_URL}/by-collaborator/${collaboratorId}`, { params });
    return response.data;
  }

  public async approvePayroll(payrollId: string): Promise<PayrollResponse> {
    const response = await hvpAuthenticatedApi.post<PayrollResponse>(
      `${API_URL}/${payrollId}/approve`
    );
    return response.data;
  }

  public async processPayment(payrollId: string): Promise<PayrollResponse> {
    const response = await hvpAuthenticatedApi.post<PayrollResponse>(
      `${API_URL}/${payrollId}/process-payment`
    );
    return response.data;
  }

  public async recalculate(
    payrollData: PayrollFormValues
  ): Promise<PayrollFormValues> {
    const request = this.transformFormValuesToRequest(payrollData);
    const response = await hvpAuthenticatedApi.post<any>(
      `${API_URL}/recalculate`,
      request
    );
    // The actual payroll data is in response.data.data
    return this.transformResponseToFormValues(response.data.data);
  }

  /**
   * Recalculate and return both V1 form values and V2 data.
   * Used by PayrollV2EditForm to update the V2 display after edits.
   */
  public async recalculateWithV2(
    payrollData: PayrollFormValues
  ): Promise<{ v1: PayrollFormValues; v2: PayrollV2 | undefined }> {
    const request = this.transformFormValuesToRequest(payrollData);
    const response = await hvpAuthenticatedApi.post<any>(
      `${API_URL}/recalculate`,
      request
    );
    const data = response.data.data;
    return {
      v1: this.transformResponseToFormValues(data),
      v2: data.payrollV2,
    };
  }

  // ============================================================
  // CFDI Methods
  // ============================================================

  public async generateCfdi(id: string): Promise<PayrollResponse> {
    await hvpAuthenticatedApi.post(`${API_URL}/${id}/generate-cfdi`);
    const response = await this.getById(id);
    return response.data;
  }

  /**
   * Rebuilds the payrollV2 employee snapshot from the collaborator's current
   * data. Used to recover a payroll that fails to stamp because the
   * collaborator's fiscal data was corrected after the payroll was created.
   */
  public async refreshSnapshot(id: string): Promise<PayrollResponse> {
    await hvpAuthenticatedApi.post(`${API_URL}/${id}/refresh-snapshot`);
    const response = await this.getById(id);
    return response.data;
  }

  public async stampCfdi(id: string): Promise<PayrollResponse> {
    const stampResponse = await hvpAuthenticatedApi.post(`${API_URL}/${id}/stamp`);
    if (stampResponse.data?.data?.cfdiStatus === "error") {
      throw new Error(stampResponse.data.data.errorMessage ?? "Error al timbrar CFDI");
    }
    const response = await this.getById(id);
    return response.data;
  }

  public async generateAndStamp(
    id: string,
    options?: { skipGenerate?: boolean }
  ): Promise<PayrollResponse> {
    if (!options?.skipGenerate) {
      await hvpAuthenticatedApi.post(`${API_URL}/${id}/generate-cfdi`);
    }
    const stampResponse = await hvpAuthenticatedApi.post(
      `${API_URL}/${id}/stamp`
    );
    if (stampResponse.data?.data?.cfdiStatus === "error") {
      throw new Error(
        stampResponse.data.data.errorMessage ?? "Error al timbrar CFDI"
      );
    }
    const response = await this.getById(id);
    return response.data;
  }

  public async cancelCfdi(id: string): Promise<PayrollResponse> {
    const cancelResponse = await hvpAuthenticatedApi.post(`${API_URL}/${id}/cancel-cfdi`);
    if (cancelResponse.data?.data?.cfdiStatus === "error") {
      throw new Error(cancelResponse.data.data.errorMessage ?? "Error al cancelar CFDI");
    }
    const response = await this.getById(id);
    return response.data;
  }

  public async downloadCfdiPdf(id: string): Promise<void> {
    const response = await hvpAuthenticatedApi.get(
      `${API_URL}/${id}/cfdi/pdf`,
      { responseType: "blob" }
    );
    this.triggerBlobDownload(response.data, `cfdi-nomina-${id}.pdf`);
  }

  public async downloadCfdiXml(id: string): Promise<void> {
    const response = await hvpAuthenticatedApi.get(
      `${API_URL}/${id}/cfdi/xml`,
      { responseType: "blob" }
    );
    this.triggerBlobDownload(response.data, `cfdi-nomina-${id}.xml`);
  }

  // ============================================================
  // V2 Edit Methods
  // ============================================================

  /**
   * Preview V2 recalculation without persisting.
   * Used by the edit form "Recalculate" button.
   */
  public async recalculateV2Preview(params: {
    collaboratorId: string;
    periodStartDate: string;
    periodEndDate: string;
    overrides: PayrollEditableOverrides;
    payrollId?: string;
  }): Promise<PayrollV2> {
    const response = await hvpAuthenticatedApi.post<{ ok: boolean; data: PayrollV2 }>(
      `${API_URL}/recalculate-v2`,
      params
    );
    return response.data.data;
  }

  /**
   * Save V2 payroll with editable overrides.
   * Replaces the old V1-based update for V2 payrolls.
   */
  public async updatePayrollV2(
    id: string,
    params: {
      payrollStatus?: string;
      periodStartDate?: string;
      periodEndDate?: string;
      overrides: PayrollEditableOverrides;
    }
  ): Promise<void> {
    await hvpAuthenticatedApi.patch(`${API_URL}/${id}/v2`, params);
  }

  // ============================================================
  // Bulk Create V2
  // ============================================================

  /**
   * Bulk create payrolls from minimal input (collaborator IDs + period).
   * Backend generates estimates and persists V1+V2 from scratch.
   */
  public async bulkCreateV2(params: {
    collaboratorIds: string[];
    periodStartDate: string;
    periodEndDate: string;
    calculateYearEndBonus?: boolean;
  }): Promise<BulkCreateV2Response> {
    const response = await hvpAuthenticatedApi.post<BulkCreateV2Response>(
      `${API_URL}/bulk-create-v2`,
      params
    );
    return response.data;
  }

  // ============================================================
  // V2 Comparison
  // ============================================================

  public async getV2Comparison(
    year: number = 2025
  ): Promise<V2ComparisonResponse> {
    const response = await hvpAuthenticatedApi.get<V2ComparisonResponse>(
      `${API_URL}/v2-comparison`,
      { params: { year } }
    );
    return response.data;
  }

  private triggerBlobDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  public async calculateSettlement(
    collaboratorId: string,
    params: {
      terminationDate: string;
      separationType: string;
      extraGratificationAmount?: number;
    }
  ): Promise<ApiResponseOne<any>> {
    const response = await hvpAuthenticatedApi.post<ApiResponseOne<any>>(
      `${API_URL}/settlement/calculate`,
      { collaboratorId, ...params }
    );
    return response.data;
  }

  public async createSettlement(
    collaboratorId: string,
    params: {
      terminationDate: string;
      separationType: string;
      extraGratificationAmount?: number;
    }
  ): Promise<ApiResponseOne<any>> {
    const response = await hvpAuthenticatedApi.post<ApiResponseOne<any>>(
      `${API_URL}/settlement/create`,
      { collaboratorId, ...params }
    );
    return response.data;
  }
}
